import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { GetPaginationRes } from '@hglobal/fin-ui';
import { map, Observable } from 'rxjs';

import { SupportIssueType } from '@zeusm/business/core/entities';
import type { Rates } from '@zeusm/business/core/entities';
import { buildBusinessApiUrl, buildBusinessApiUrlV2 } from '@zeusm/shared/core/helpers';

import { ShellApi } from './shell-api.interface';
import { ApiSettings, ConditionsResponse } from '../entities';

@Injectable()
export class ShellApiService implements ShellApi {
  readonly #http = inject(HttpClient);

  getRates(): Observable<GetPaginationRes<Rates>> {
    const api = buildBusinessApiUrl('settings/currencies/rates');

    return this.#http.get<Rates[]>(api).pipe(
      map((ratesList: Rates[]) => {
        const paginatedRates: GetPaginationRes<Rates> = {
          items: ratesList.filter(rate => rate.info?.length),
        };

        return paginatedRates;
      }),
    );
  }

  getCryptoMarket(): Observable<GetPaginationRes<any>> {
    const api = buildBusinessApiUrl('settings/crypto-market');

    return this.#http.get<GetPaginationRes<any>>(api);
  }

  getTotalBalance(currencyId: number): Observable<string> {
    return this.#http
      .get<any>(buildBusinessApiUrl('dashboard/balance/total'), {
        params: { currency_id: currencyId },
      })
      .pipe(map(res => res.balance));
  }

  getSettings(): Observable<ApiSettings> {
    return this.#http.get<ApiSettings>(buildBusinessApiUrlV2('settings'));
  }

  getHasAgentAccounts({ orgId }: { orgId: string }): Observable<boolean> {
    return this.#http
      .get<{
        hasAgentAccounts: boolean;
      }>(buildBusinessApiUrlV2('agent_accounts/org_has_purchases/'), { params: { orgId } })
      .pipe(map(res => res?.hasAgentAccounts));
  }

  getConditions(): Observable<ConditionsResponse> {
    return this.#http.get<ConditionsResponse>(buildBusinessApiUrl('settings/fee'));
  }

  generateTelegramLink(): Observable<string> {
    return this.#http.post<{ url: string }>(buildBusinessApiUrl('settings/support/t/key/generate/'), null).pipe(map(res => res.url));
  }

  getSupportLink(issueType: SupportIssueType): Observable<{ url: string }> {
    return this.#http.get<{ url: string }>(buildBusinessApiUrl('accounts/support/telegram'), {
      params: { issueType },
    });
  }
}
